<script setup lang="ts">
/** Statistiques d'accompagnement — délais moyens et répartitions sur une période. */
import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import {
  ArrowLeftIcon,
  ChartBarIcon,
  ClockIcon,
  BoltIcon,
  InboxStackIcon,
  BanknotesIcon,
  TrophyIcon,
} from '@heroicons/vue/24/outline'
import { useAccompagnementStore } from '@/v2/accompagnement/store/useAccompagnementStore'
import {
  ACCOMP_STATUS_META,
  ACCOMP_STATUS_ORDER,
  ACCOMP_URGENCY_META,
  ACCOMP_URGENCY_ORDER,
} from '@/v2/accompagnement/constants/accompagnementConstants'
import { formatDuration, formatMinutes } from '@/v2/accompagnement/helpers/accompagnementDelays'
import { formatDateShort, formatEuros, toDateInputValue } from '@/v2/accompagnement/helpers/accompagnementFormat'
import AccompStatusBreakdown from '@/v2/accompagnement/components/AccompStatusBreakdown.vue'

const store = useAccompagnementStore()
const { stats, statsLoading } = storeToRefs(store)

const from = ref('')
const to = ref('')

/** Taux de transformation = gagnées / (gagnées + perdues). */
const wonLost = computed(() => {
  const won = stats.value?.byStatus.GAGNEE ?? 0
  const lost = stats.value?.byStatus.PERDUE ?? 0
  return { won, lost, rate: won + lost > 0 ? won / (won + lost) : null }
})

function formatPercent(r: number | null): string {
  return r == null ? '—' : `${Math.round(r * 100)} %`
}

/** Somme des demandes sur tous les statuts d'une ligne. */
function rowTotal(byStatus: Record<string, number>): number {
  return ACCOMP_STATUS_ORDER.reduce((sum, s) => sum + (byStatus[s] ?? 0), 0)
}

/** Ligne « Total général » des techniciens. */
const techTotals = computed(() => {
  const byStatus: Record<string, number> = {}
  for (const s of ACCOMP_STATUS_ORDER) byStatus[s] = 0
  let spent = 0
  let ca = 0
  let marge = 0
  for (const t of stats.value?.perTechnician ?? []) {
    for (const s of ACCOMP_STATUS_ORDER) byStatus[s] += t.byStatus[s]
    spent += t.timeSpentMinutes
    ca += t.caWon
    marge += t.margeWon
  }
  return { byStatus, total: rowTotal(byStatus), spent, ca, marge }
})

/** Ligne « Total général » des demandeurs. */
const reqTotals = computed(() => {
  const byStatus: Record<string, number> = {}
  for (const s of ACCOMP_STATUS_ORDER) byStatus[s] = 0
  let spent = 0
  let ca = 0
  let marge = 0
  for (const r of stats.value?.perRequester ?? []) {
    for (const s of ACCOMP_STATUS_ORDER) byStatus[s] += r.byStatus[s]
    spent += r.timeSpentMinutes
    ca += r.caWon
    marge += r.margeWon
  }
  return { byStatus, total: rowTotal(byStatus), spent, ca, marge }
})

function load() {
  if (!from.value || !to.value) return
  store.fetchStats({ from: from.value, to: `${to.value}T23:59:59` })
}

onMounted(() => {
  const now = new Date()
  const past = new Date(now.getTime() - 90 * 24 * 3_600_000)
  to.value = toDateInputValue(now.toISOString())
  from.value = toDateInputValue(past.toISOString())
  load()
})
</script>

<template>
  <div class="mx-auto w-full max-w-6xl p-4 md:p-8">
    <RouterLink to="/accompagnement" class="mb-3 inline-flex items-center gap-1 text-sm text-base-content/70 hover:text-primary">
      <ArrowLeftIcon class="h-4 w-4" aria-hidden="true" />
      Retour aux demandes
    </RouterLink>

    <header class="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 class="flex items-center gap-2 text-2xl font-bold">
          <ChartBarIcon class="h-6 w-6 text-primary" aria-hidden="true" />
          Accompagnement — statistiques
        </h1>
        <p class="mt-1 text-base-content/70">Délais moyens (heures ouvrées) et répartition des demandes.</p>
      </div>
      <div class="flex items-end gap-2">
        <div>
          <label for="stat-from" class="mb-1 block text-sm font-medium text-base-content">Du</label>
          <input id="stat-from" v-model="from" type="date" class="input input-bordered" @change="load" />
        </div>
        <div>
          <label for="stat-to" class="mb-1 block text-sm font-medium text-base-content">Au</label>
          <input id="stat-to" v-model="to" type="date" class="input input-bordered" @change="load" />
        </div>
      </div>
    </header>

    <div v-if="statsLoading && !stats" class="py-16 text-center">
      <span class="loading loading-spinner" aria-hidden="true"></span>
      <p class="mt-2 text-base-content/70">Calcul des statistiques…</p>
    </div>

    <template v-else-if="stats">
      <!-- KPI -->
      <div class="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        <div class="card border-l-4 border-secondary bg-base-100 shadow-sm">
          <div class="card-body gap-0.5 p-3">
            <p class="flex items-center gap-1.5 text-sm font-semibold text-base-content/70">
              <InboxStackIcon class="h-4 w-4" aria-hidden="true" /> Demandes
            </p>
            <p class="text-xl font-bold tabular-nums">{{ stats.total }}</p>
          </div>
        </div>
        <div class="card border-l-4 border-secondary bg-base-100 shadow-sm">
          <div class="card-body gap-0.5 p-3">
            <p class="flex items-center gap-1.5 text-sm font-semibold text-base-content/70">
              <ClockIcon class="h-4 w-4" aria-hidden="true" /> Délai d'attribution
            </p>
            <p class="text-xl font-bold tabular-nums">{{ formatDuration(stats.avgAssignDelayMs) }}</p>
            <p class="text-sm text-base-content/70">moyenne</p>
          </div>
        </div>
        <div class="card border-l-4 border-primary bg-base-100 shadow-sm">
          <div class="card-body gap-0.5 p-3">
            <p class="flex items-center gap-1.5 text-sm font-semibold text-base-content/70">
              <BoltIcon class="h-4 w-4" aria-hidden="true" /> Prise en charge
            </p>
            <p class="text-xl font-bold tabular-nums">{{ formatDuration(stats.avgAcceptDelayMs) }}</p>
            <p class="text-sm text-base-content/70">moyenne</p>
          </div>
        </div>
        <div class="card border-l-4 border-error bg-base-100 shadow-sm">
          <div class="card-body gap-0.5 p-3">
            <p class="text-sm font-semibold text-base-content/70">Prioritaires</p>
            <p class="text-xl font-bold tabular-nums">{{ stats.byUrgency.PRIORITAIRE }}</p>
            <p class="text-sm text-base-content/70">sur {{ stats.total }}</p>
          </div>
        </div>
      </div>

      <!-- KPI business : CA / marge gagnés + taux de transformation -->
      <div class="mt-2.5 grid grid-cols-2 gap-2.5 lg:grid-cols-3">
        <div class="card border-l-4 border-success bg-base-100 shadow-sm">
          <div class="card-body gap-0.5 p-3">
            <p class="flex items-center gap-1.5 text-sm font-semibold text-base-content/70">
              <BanknotesIcon class="h-4 w-4" aria-hidden="true" /> CA gagné
            </p>
            <p class="text-xl font-bold tabular-nums">{{ formatEuros(stats.caWon) }}</p>
            <p class="text-sm text-base-content/70">en cours : {{ formatEuros(stats.caPending) }}</p>
          </div>
        </div>
        <div class="card border-l-4 border-success bg-base-100 shadow-sm">
          <div class="card-body gap-0.5 p-3">
            <p class="flex items-center gap-1.5 text-sm font-semibold text-base-content/70">
              <BanknotesIcon class="h-4 w-4" aria-hidden="true" /> Marge gagnée
            </p>
            <p class="text-xl font-bold tabular-nums">{{ formatEuros(stats.margeWon) }}</p>
            <p class="text-sm text-base-content/70">en cours : {{ formatEuros(stats.margePending) }}</p>
          </div>
        </div>
        <div class="card border-l-4 border-primary bg-base-100 shadow-sm">
          <div class="card-body gap-0.5 p-3">
            <p class="flex items-center gap-1.5 text-sm font-semibold text-base-content/70">
              <TrophyIcon class="h-4 w-4" aria-hidden="true" /> Taux de transformation
            </p>
            <p class="text-xl font-bold tabular-nums">{{ formatPercent(wonLost.rate) }}</p>
            <p class="text-sm text-base-content/70">{{ wonLost.won }} gagnées / {{ wonLost.lost }} perdues</p>
          </div>
        </div>
      </div>

      <!-- Répartitions -->
      <div class="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <section class="card border border-base-300 bg-base-100 shadow-sm">
          <div class="card-body gap-3 p-4 md:p-6">
            <h2 class="card-title text-lg font-semibold">Par statut</h2>
            <ul role="list" class="space-y-2">
              <li v-for="s in ACCOMP_STATUS_ORDER" :key="s" class="flex items-center justify-between">
                <span class="badge badge-sm" :class="ACCOMP_STATUS_META[s].badgeClass">{{ ACCOMP_STATUS_META[s].label }}</span>
                <span class="font-semibold tabular-nums">{{ stats.byStatus[s] }}</span>
              </li>
            </ul>
          </div>
        </section>

        <section class="card border border-base-300 bg-base-100 shadow-sm">
          <div class="card-body gap-3 p-4 md:p-6">
            <h2 class="card-title text-lg font-semibold">Par niveau d'urgence</h2>
            <ul role="list" class="space-y-2">
              <li v-for="u in ACCOMP_URGENCY_ORDER" :key="u" class="flex items-center justify-between">
                <span class="badge badge-sm" :class="ACCOMP_URGENCY_META[u].badgeClass">{{ ACCOMP_URGENCY_META[u].label }}</span>
                <span class="font-semibold tabular-nums">{{ stats.byUrgency[u] }}</span>
              </li>
            </ul>
          </div>
        </section>
      </div>

      <!-- Par technicien -->
      <section class="mt-5 card border border-base-300 bg-base-100 shadow-sm">
        <div class="card-body gap-3 p-4 md:p-6">
          <h2 class="card-title text-lg font-semibold">Par technicien</h2>
          <!-- Tableau (≥ md) -->
          <div class="hidden overflow-x-auto md:block">
            <table class="table">
              <thead>
                <tr>
                  <th scope="col">Technicien</th>
                  <th v-for="s in ACCOMP_STATUS_ORDER" :key="s" scope="col" class="text-center">
                    {{ ACCOMP_STATUS_META[s].label }}
                  </th>
                  <th scope="col" class="text-center">Total</th>
                  <th scope="col">Temps passé</th>
                  <th scope="col">CA gagné</th>
                  <th scope="col">Marge</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!stats.perTechnician.length">
                  <td :colspan="ACCOMP_STATUS_ORDER.length + 5" class="py-6 text-center text-base-content/70">
                    Aucune demande assignée sur la période.
                  </td>
                </tr>
                <tr v-for="t in stats.perTechnician" :key="t.uuid">
                  <td class="font-medium">{{ t.name }}</td>
                  <td v-for="s in ACCOMP_STATUS_ORDER" :key="s" class="text-center tabular-nums">
                    {{ t.byStatus[s] || '—' }}
                  </td>
                  <td class="text-center font-semibold tabular-nums">{{ rowTotal(t.byStatus) }}</td>
                  <td class="tabular-nums">{{ t.timeSpentMinutes > 0 ? formatMinutes(t.timeSpentMinutes) : '—' }}</td>
                  <td class="tabular-nums">{{ formatEuros(t.caWon) }}</td>
                  <td class="tabular-nums">{{ formatEuros(t.margeWon) }}</td>
                </tr>
              </tbody>
              <tfoot v-if="stats.perTechnician.length">
                <tr class="font-semibold">
                  <td>Total général</td>
                  <td v-for="s in ACCOMP_STATUS_ORDER" :key="s" class="text-center tabular-nums">
                    {{ techTotals.byStatus[s] || '—' }}
                  </td>
                  <td class="text-center tabular-nums">{{ techTotals.total }}</td>
                  <td class="tabular-nums">{{ techTotals.spent > 0 ? formatMinutes(techTotals.spent) : '—' }}</td>
                  <td class="tabular-nums">{{ formatEuros(techTotals.ca) }}</td>
                  <td class="tabular-nums">{{ formatEuros(techTotals.marge) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <!-- Liste (mobile < md) -->
          <ul role="list" class="flex flex-col gap-2 md:hidden">
            <li v-if="!stats.perTechnician.length" class="py-6 text-center text-base-content/70">
              Aucune demande assignée sur la période.
            </li>
            <li v-for="t in stats.perTechnician" :key="t.uuid" class="rounded-lg border border-base-200 p-3">
              <p class="font-medium text-base-content">{{ t.name }}</p>
              <dl class="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                <div class="flex gap-1">
                  <dt class="text-base-content/70">Total :</dt>
                  <dd class="tabular-nums text-base-content">{{ rowTotal(t.byStatus) }}</dd>
                </div>
                <div class="flex gap-1">
                  <dt class="text-base-content/70">Temps passé :</dt>
                  <dd class="tabular-nums text-base-content">
                    {{ t.timeSpentMinutes > 0 ? formatMinutes(t.timeSpentMinutes) : '—' }}
                  </dd>
                </div>
                <div class="flex gap-1">
                  <dt class="text-base-content/70">CA gagné :</dt>
                  <dd class="tabular-nums text-base-content">{{ formatEuros(t.caWon) }}</dd>
                </div>
                <div class="flex gap-1">
                  <dt class="text-base-content/70">Marge :</dt>
                  <dd class="tabular-nums text-base-content">{{ formatEuros(t.margeWon) }}</dd>
                </div>
              </dl>
              <AccompStatusBreakdown :by-status="t.byStatus" class="mt-2" />
            </li>
          </ul>
        </div>
      </section>

      <!-- Par demandeur -->
      <section class="mt-5 card border border-base-300 bg-base-100 shadow-sm">
        <div class="card-body gap-3 p-4 md:p-6">
          <h2 class="card-title text-lg font-semibold">Par demandeur</h2>
          <!-- Tableau (≥ md) -->
          <div class="hidden overflow-x-auto md:block">
            <table class="table">
              <thead>
                <tr>
                  <th scope="col">Demandeur</th>
                  <th v-for="s in ACCOMP_STATUS_ORDER" :key="s" scope="col" class="text-center">
                    {{ ACCOMP_STATUS_META[s].label }}
                  </th>
                  <th scope="col" class="text-center">Total</th>
                  <th scope="col">Temps passé</th>
                  <th scope="col">CA gagné</th>
                  <th scope="col">Marge</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!stats.perRequester.length">
                  <td :colspan="ACCOMP_STATUS_ORDER.length + 5" class="py-6 text-center text-base-content/70">
                    Aucune demande sur la période.
                  </td>
                </tr>
                <tr v-for="r in stats.perRequester" :key="r.uuid">
                  <td class="font-medium">{{ r.name }}</td>
                  <td v-for="s in ACCOMP_STATUS_ORDER" :key="s" class="text-center tabular-nums">
                    {{ r.byStatus[s] || '—' }}
                  </td>
                  <td class="text-center font-semibold tabular-nums">{{ rowTotal(r.byStatus) }}</td>
                  <td class="tabular-nums">{{ r.timeSpentMinutes > 0 ? formatMinutes(r.timeSpentMinutes) : '—' }}</td>
                  <td class="tabular-nums">{{ formatEuros(r.caWon) }}</td>
                  <td class="tabular-nums">{{ formatEuros(r.margeWon) }}</td>
                </tr>
              </tbody>
              <tfoot v-if="stats.perRequester.length">
                <tr class="font-semibold">
                  <td>Total général</td>
                  <td v-for="s in ACCOMP_STATUS_ORDER" :key="s" class="text-center tabular-nums">
                    {{ reqTotals.byStatus[s] || '—' }}
                  </td>
                  <td class="text-center tabular-nums">{{ reqTotals.total }}</td>
                  <td class="tabular-nums">{{ reqTotals.spent > 0 ? formatMinutes(reqTotals.spent) : '—' }}</td>
                  <td class="tabular-nums">{{ formatEuros(reqTotals.ca) }}</td>
                  <td class="tabular-nums">{{ formatEuros(reqTotals.marge) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <!-- Liste (mobile < md) -->
          <ul role="list" class="flex flex-col gap-2 md:hidden">
            <li v-if="!stats.perRequester.length" class="py-6 text-center text-base-content/70">
              Aucune demande sur la période.
            </li>
            <li v-for="r in stats.perRequester" :key="r.uuid" class="rounded-lg border border-base-200 p-3">
              <p class="font-medium text-base-content">{{ r.name }}</p>
              <dl class="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                <div class="flex gap-1">
                  <dt class="text-base-content/70">Demandes :</dt>
                  <dd class="tabular-nums text-base-content">{{ r.total }}</dd>
                </div>
                <div class="flex gap-1">
                  <dt class="text-base-content/70">Temps passé :</dt>
                  <dd class="tabular-nums text-base-content">
                    {{ r.timeSpentMinutes > 0 ? formatMinutes(r.timeSpentMinutes) : '—' }}
                  </dd>
                </div>
                <div class="flex gap-1">
                  <dt class="text-base-content/70">CA gagné :</dt>
                  <dd class="tabular-nums text-base-content">{{ formatEuros(r.caWon) }}</dd>
                </div>
                <div class="flex gap-1">
                  <dt class="text-base-content/70">Marge :</dt>
                  <dd class="tabular-nums text-base-content">{{ formatEuros(r.margeWon) }}</dd>
                </div>
                <div class="flex gap-1">
                  <dt class="text-base-content/70">Dernière :</dt>
                  <dd class="tabular-nums text-base-content">{{ formatDateShort(r.lastCreatedAt) }}</dd>
                </div>
              </dl>
              <AccompStatusBreakdown :by-status="r.byStatus" class="mt-2" />
            </li>
          </ul>
        </div>
      </section>
    </template>
  </div>
</template>
